/**
 * Builds the seat fields embedded into a booking's `flight_data` so seat-map
 * selections survive checkout and reach the Bookings dashboard.
 *
 * Why: SelectSeats → Review carries `seatSelections` as an array indexed by
 * merged segment order ([{ '14A': {...} }, {}]), but every checkout path
 * (Reserve, Defera Zero/Capital, Slice Pay/miles via the cart) previously
 * built the booking's `flight_data` from `selectedFlight` alone, dropping the
 * picks. The dashboard (FlightDetailsModal) renders seats from
 * `flight_data.seat_assignments` keyed by passenger DB id — ids that don't
 * exist until the server inserts passenger rows.
 *
 * So this returns two additive fields:
 *  - `seat_selections_detail`: { [segmentId]: { [designator]: {…} } } — full
 *    audit trail (price, cabin, position) for concierge fulfillment.
 *  - `seat_assignments_by_index`: { [passengerIndex]: { [segmentId]: '14A WINDOW' } }
 *    — the server maps passenger index → inserted passenger row id right
 *    after creating the rows, producing the `seat_assignments` shape the
 *    dashboard already renders.
 *
 * Segment keys use the merged flight's segment ids (Review merges the return
 * offer's slices into `selectedFlight`, so flat slice order matches the
 * SelectSeats merge order); falls back to `segment_<idx>` when ids are absent.
 *
 * Returns {} when nothing was selected, so call sites can spread the result
 * unconditionally without polluting `flight_data`.
 */
export function buildSeatDataForFlightData(seatSelections, mergedFlight) {
  const selections = Array.isArray(seatSelections) ? seatSelections : [];
  const hasAny = selections.some(
    (s) => s && typeof s === 'object' && Object.keys(s).length > 0
  );
  if (!hasAny) return {};

  const mergedSegments = (mergedFlight?.slices || []).flatMap(
    (slice) => slice?.segments || []
  );

  const bySegment = {};
  const byPassengerIndex = {};

  selections.forEach((segmentSeats, idx) => {
    if (!segmentSeats || typeof segmentSeats !== 'object') return;
    const entries = Object.entries(segmentSeats);
    if (entries.length === 0) return;

    const segmentKey = mergedSegments[idx]?.id || `segment_${idx}`;

    entries.forEach(([designator, sel]) => {
      const passengerIndex = sel?.passengerId ?? 0;

      if (!bySegment[segmentKey]) bySegment[segmentKey] = {};
      bySegment[segmentKey][designator] = {
        passenger_index: passengerIndex,
        position: sel?.position || null,
        price: sel?.price || null,
        cabin: sel?.cabin || null,
      };

      // "14A WINDOW" — the label format SegmentSeatChips already parses
      const label = sel?.position
        ? `${designator} ${String(sel.position).toUpperCase()}`
        : designator;
      if (!byPassengerIndex[passengerIndex]) byPassengerIndex[passengerIndex] = {};
      byPassengerIndex[passengerIndex][segmentKey] = label;
    });
  });

  if (Object.keys(bySegment).length === 0) return {};

  return {
    seat_selections_detail: bySegment,
    seat_assignments_by_index: byPassengerIndex,
  };
}
